import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getCarById, getOtherCars } from '../data/cars';
import {
  formatInstallmentLabel,
  getEstimatedInstallment,
  parsePrice,
} from '../utils/loanCalculator';
import '../App.css';
import './CarDetailPage.css';

const CarDetailPage = () => {
  const { carId } = useParams();
  const car = getCarById(carId);
  const otherCars = getOtherCars(carId);
  const [activeTab, setActiveTab] = useState('exterior');
  const [gearUpEnabled, setGearUpEnabled] = useState(false);
  const [selectedColor, setSelectedColor] = useState(car?.colors?.[0] || null);

  if (!car) {
    return (
      <div>
        <Header />
        <div className="section" style={{ textAlign: 'center', padding: '100px 5%' }}>
          <h1>Car Not Found</h1>
          <p>The car you're looking for doesn't exist.</p>
          <Link to="/" className="btn-fill" style={{ display: 'inline-block', marginTop: '20px' }}>
            Back to Home
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const safetyImageSrc = `/features/${car.id}/safety.webp`;
  const safetyImageFallbackSrc = '/features/axia/safety.webp';

  const technologyImageSrcWebp = `/features/${car.id}/technology.webp`;
  const technologyImageSrcPng = `/features/${car.id}/technology.png`;
  const technologyImageFallbackSrc = '/features/ativa/technology.webp';

  return (
    <div className="car-detail-page">
      <Header />

      {/* Hero Section */}
      <section className="car-hero" style={{ backgroundImage: `url('/${car.imageinfo}')` }}>
        {/* <div className="hero-overlay">
          <div className="hero-content">
            <h1>{car.name}</h1>
            <h2 className="car-tagline">{car.tagline}</h2>
            <p className="car-description">{car.description}</p>
            <p className="car-price-hero">Price starting from {car.price}</p>
            <div className="hero-actions">
              <Link to="/#advisors" className="btn-fill">Book Now</Link>
              <a href="#pricing" className="btn-outline">View Pricing</a>
            </div>
          </div>
        </div> */}
      </section>

      {/* Safety & Security Section */}
      <section className="section safety-section">
        <div className="section-title">
          <h2>SAFETY & SECURITY</h2>
          {/* <p>Raising the bar for the safety of everyone, both in and around the car.</p> */}
        </div>
        {/* <div className="safety-grid">
          {car.safety.map((feature, index) => (
            <div key={index} className="safety-item">
              <div className="safety-icon">✓</div>
              <p>{feature}</p>
            </div>
          ))}
        </div> */}
        <div className="detail-media">
          <img
            className="detail-media-image"
            src={safetyImageSrc}
            alt={`${car.name} safety`}
            loading="lazy"
            onError={(e) => {
              if (e.currentTarget.src.endsWith(safetyImageFallbackSrc)) return;
              e.currentTarget.src = safetyImageFallbackSrc;
            }}
          />
        </div>
      </section>

      {/* Efficiency Section */}
      <section className="section efficiency-section">
        <div className="section-title">
          <h2>TECHNOLOGY</h2>
          {/* <p>Raising the bar for the safety of everyone, both in and around the car.</p> */}
        </div>
        <div className="detail-media">
          <img
            className="detail-media-image"
            src={technologyImageSrcWebp}
            alt={`${car.name} technology`}
            loading="lazy"
            onError={(e) => {
              const currentSrc = e.currentTarget.getAttribute('src') || '';
              if (currentSrc === technologyImageSrcWebp) {
                e.currentTarget.src = technologyImageSrcPng;
                return;
              }
              if (currentSrc === technologyImageSrcPng) {
                e.currentTarget.src = technologyImageFallbackSrc;
                return;
              }
              if (e.currentTarget.src.endsWith(technologyImageFallbackSrc)) return;
              e.currentTarget.src = technologyImageFallbackSrc;
            }}
          />
        </div>
      </section>

      {/* Exterior & Interior Section */}
      <section className="section exterior-interior-section">
        <div className="section-title">
          <h2>EXTERIOR & INTERIOR</h2>
          {/* <p>Your car should reflect your style. We made sure that no corners were cut when designing our vehicles.</p> */}
        </div>

        <div className="tab-buttons">
          <button
            className={activeTab === 'exterior' ? 'active' : ''}
            onClick={() => setActiveTab('exterior')}
          >
            EXTERIOR
          </button>
          <button
            className={activeTab === 'interior' ? 'active' : ''}
            onClick={() => setActiveTab('interior')}
          >
            INTERIOR
          </button>
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={gearUpEnabled}
              onChange={(e) => setGearUpEnabled(e.target.checked)}
            />
            <span className="toggle-slider"></span>
            <span className="toggle-label">GearUp Features</span>
          </label>
        </div>


        {/* Color Selector - Only show on exterior tab */}
        {activeTab === 'exterior' && car.colors && (
          <div className="color-selector-section">

            <div className="controls-section">

              {activeTab === 'exterior' && car.colors && (
                <div className="color-switcher">
                  <div className="color-options">
                    {car.colors.map((color, index) => (
                      <button
                        key={index}
                        className={`color-option ${selectedColor?.name === color.name ? 'active' : ''}`}
                        onClick={() => setSelectedColor(color)}
                        title={color.name}
                      >
                        <span
                          className="color-swatch"
                          style={{ backgroundColor: color.hex }}
                        ></span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="car-color-name">
              {selectedColor?.name || car.colors[0].name}
            </div>
            <div className="car-display">
              <img
                src={`/${selectedColor?.image || car.colors[0].image}`}
                alt={`${car.name} - ${selectedColor?.name || car.colors[0].name}`}
                className="car-display-image"
              />
            </div>
          </div>
        )}

        <div className="features-grid">
          {activeTab === 'exterior' ? (
            <>
              {gearUpEnabled && car.gearupExterior && car.gearupExterior.map((feature, index) => (
                <div key={`gearup-ext-${index}`} className="feature-item gearup-feature">
                  <div className="feature-image">
                    <img src={`/${feature.image}`} alt={feature.name} />
                  </div>
                  <h4>{feature.name}</h4>
                  <span className="gearup-badge">GearUp</span>
                </div>
              ))}
              {car.exterior.map((feature, index) => (
                <div key={`ext-${index}`} className="feature-item">
                  <div className="feature-image">
                    <img src={`/${feature.image}`} alt={feature.name} />
                  </div>
                  <h4>{feature.name}</h4>
                </div>
              ))}
            </>
          ) : (
            <>
              {gearUpEnabled && car.gearupInterior && car.gearupInterior.map((feature, index) => (
                <div key={`gearup-int-${index}`} className="feature-item gearup-feature">
                  <div className="feature-image">
                    <img src={`/${feature.image}`} alt={feature.name} />
                  </div>
                  <h4>{feature.name}</h4>
                  <span className="gearup-badge">GearUp</span>
                </div>
              ))}
              {car.interior.map((feature, index) => (
                <div key={`int-${index}`} className="feature-item">
                  <div className="feature-image">
                    <img src={`/${feature.image}`} alt={feature.name} />
                  </div>
                  <h4>{feature.name}</h4>
                </div>
              ))}
            </>
          )}
        </div>
      </section>

      {/* Key Features Section */}
      <section className="section features-section">
        <div className="section-title">
          <h2>Why Choose Perodua {car.name}?</h2>
        </div>
        <div className="features-grid-detailed">
          {car.features.map((feature, index) => (
            <div key={index} className="feature-box">
              <h4>{feature.title}</h4>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Price & Specification Section */}
      <section id="pricing" className="section pricing-section">
        <div className="section-title">
          <h2>PRICE & SPECIFICATION</h2>
          {/* <p>On the road price with Sales Tax and without insurance.</p> */}
        </div>
        <div className="variants-grid">
          {car.variants.map((variant, index) => {
            const installmentLabel =
              variant.installment && variant.installment.trim() !== ''
                ? variant.installment
                : formatInstallmentLabel(
                    getEstimatedInstallment(parsePrice(variant.price))
                  );

            return (
            <div key={index} className="variant-card">
              <h3>{variant.name}</h3>
              <div className="variant-price">{variant.price}</div>
              <div className="variant-installment">
                Est. {installmentLabel}
              </div>
              <ul className="variant-features">
                {variant.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
              <div className="variant-actions">
                <Link to="/#advisors" className="btn-fill">Book Now</Link>
                <Link
                  to={`/?car=${car.id}&variant=${index}#calculator`}
                  className="btn-outline variant-calc-link"
                >
                  Calculate installment
                </Link>
              </div>
            </div>
            );
          })}
        </div>
      </section>

      {/* Explore Other Models Section */}
      <section className="section other-models-section">
        <div className="section-title">
          <h2>EXPLORE OTHER MODELS</h2>
          <p>Discover other Perodua models that fit your lifestyle.</p>
        </div>
        <div className="models-grid">
          {otherCars.map((otherCar) => (
            <Link key={otherCar.id} to={`/car/${otherCar.id}`} className="car-card-link">
              <div className="car-card">
                <div className="car-img" style={{ backgroundImage: `url('/${otherCar.image}')` }}></div>
                <div className="car-info">
                  <div className="car-title">{otherCar.name}</div>
                  <div className="car-price">Starting From {otherCar.price}</div>
                  <div className="card-actions">
                    <span className="btn-outline">Learn More</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CarDetailPage;
